/**
 * Datadog RUM (Real User Monitoring) Initialization Component
 *
 * This component initializes Datadog RUM for the Next.js application.
 * It must be rendered client-side only and should be placed high in the component tree.
 *
 * Features:
 * - Session tracking with configurable sampling rates
 * - Session replay recording (disabled by default)
 * - User interaction tracking
 * - Resource and long task monitoring
 * - Backend trace correlation with APM
 * - Route change tracking for SPA navigation
 *
 * Environment Variables Required:
 * - NEXT_PUBLIC_DD_RUM_ENABLED: Enable/disable RUM (true/false)
 * - NEXT_PUBLIC_DD_APP_ID: Datadog application ID
 * - NEXT_PUBLIC_DD_CLIENT_TOKEN: Datadog client token
 * - NEXT_PUBLIC_DD_SITE: Datadog site (e.g., datadoghq.com)
 * - NEXT_PUBLIC_DD_SERVICE: Service name (e.g., showpass-frontend)
 * - NEXT_PUBLIC_DD_ENV: Environment name (e.g., preview, beta, production)
 * - NEXT_PUBLIC_APP_VERSION: Application version
 * - NEXT_PUBLIC_DD_SESSION_SAMPLE_RATE: Session sampling rate (0-100)
 * - NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE: Session replay sampling rate (0-100)
 * - NEXT_PUBLIC_DD_TRACK_USER_INTERACTIONS: Track clicks and interactions (true/false)
 * - NEXT_PUBLIC_DD_TRACK_RESOURCES: Track XHR/Fetch requests (true/false)
 * - NEXT_PUBLIC_DD_TRACK_LONG_TASKS: Track long tasks (true/false)
 */

import { datadogRum } from "@datadog/browser-rum";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import {
  APP_VERSION,
  DD_APP_ID,
  DD_CLIENT_TOKEN,
  DD_ENV,
  DD_RUM_ENABLED,
  DD_SERVICE,
  DD_SESSION_REPLAY_SAMPLE_RATE,
  DD_SESSION_SAMPLE_RATE,
  DD_SITE,
  DD_TRACK_LONG_TASKS,
  DD_TRACK_RESOURCES,
  DD_TRACK_USER_INTERACTIONS,
  INTERNAL_SHOWPASS_API_URL,
} from "@shared/env";

/**
 * DatadogRUM component initializes Datadog Real User Monitoring
 * Should be rendered once at the application root level
 */
const DatadogRUM: React.FC = () => {
  const router = useRouter();
  const isInitialized = useRef(false);

  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") {
      return;
    }

    // Check if RUM is enabled
    const isRumEnabled = DD_RUM_ENABLED === "true";

    if (!isRumEnabled) {
      return;
    }

    // Prevent multiple initializations
    if (isInitialized.current) {
      return;
    }

    // Validate required environment variables
    const appId = DD_APP_ID;
    const clientToken = DD_CLIENT_TOKEN;
    const site = DD_SITE || "datadoghq.com";
    const service = DD_SERVICE || "showpass-frontend";
    const env = DD_ENV || "preview";
    const version = APP_VERSION || "unknown";

    if (!appId || !clientToken) {
      // eslint-disable-next-line no-console
      console.warn(
        "Datadog RUM: Missing required environment variables (DD_APP_ID or DD_CLIENT_TOKEN)"
      );
      return;
    }

    // Check if already initialized (in case of hot reload in development)
    if (appId && clientToken) {
      try {
        // Parse sampling rates
        const sessionSampleRate = parseInt(DD_SESSION_SAMPLE_RATE || "100", 10);
        const sessionReplaySampleRate = parseInt(
          DD_SESSION_REPLAY_SAMPLE_RATE || "0",
          10
        );

        // Parse tracking flags
        const trackUserInteractions = DD_TRACK_USER_INTERACTIONS === "true";
        const trackResources = DD_TRACK_RESOURCES === "true";
        const trackLongTasks = DD_TRACK_LONG_TASKS === "true";

        // Initialize Datadog RUM
        datadogRum.init({
          applicationId: appId,
          clientToken,
          site,
          service,
          env,
          version,
          sessionSampleRate,
          sessionReplaySampleRate,
          trackUserInteractions,
          trackResources,
          trackLongTasks,
          defaultPrivacyLevel: "mask-user-input", // Mask user input for privacy
          // Enable trace correlation with backend APM
          allowedTracingUrls: [
            {
              match: (url: string) => {
                // Match API calls to your backend
                return (
                  url.includes(INTERNAL_SHOWPASS_API_URL || "") ||
                  url.includes(window.location.origin)
                );
              },
              propagatorTypes: ["datadog", "tracecontext"],
            },
          ],
          // Before sending data, you can filter or modify
          beforeSend: (event) => {
            // Filter out sensitive data if needed
            // For example, mask email addresses in URLs
            if (event.view?.url) {
              // eslint-disable-next-line no-param-reassign
              event.view.url = event.view.url.replace(
                /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
                "[REDACTED_EMAIL]"
              );
            }
            return true; // Return false to discard the event
          },
        });

        // Start session replay if enabled
        if (sessionReplaySampleRate > 0) {
          datadogRum.startSessionReplayRecording();
        }

        isInitialized.current = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to initialize Datadog RUM:", error);
      }
    }
  }, []);

  // Track route changes
  useEffect(() => {
    if (!isInitialized.current) {
      return;
    }

    const handleRouteChange = (url: string) => {
      // Datadog automatically tracks route changes, but we can add custom context
      datadogRum.addAction("route_change", {
        from: router.asPath,
        to: url,
      });
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    // eslint-disable-next-line consistent-return
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router]);

  // This component doesn't render anything
  return null;
};

export default DatadogRUM;
